'use client'

import React, { FC, ReactNode, CSSProperties, useRef, isValidElement, ReactElement } from 'react';
import { useInView, motion, Variants } from 'motion/react';

interface FramerRevealProps {
  children: ReactNode;
  delay?: number;
  staggerDelay?: number;
  y?: number;
  opacity?: number;
  className?: string;
  style?: CSSProperties;
}

export const FramerReveal: FC<FramerRevealProps> = ({ 
  children, 
  delay = 0, 
  staggerDelay = 0.2, 
  y = 20, 
  opacity = 0.8,
  className = '',
  style = {} 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.1
  });

  const childVariants: Variants = {
    hidden: { 
      y,
      opacity,
      transition: { duration: 0 }
    },
    visible: (index: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: delay + (index * staggerDelay),
        duration: 0.5,
        ease: 'easeOut'
      }
    })
  };

  const renderChildren = (children: ReactNode) => {
    return React.Children.map(children, (child, index) => {
      if (isValidElement(child) && (child.type === 'div')) {
        const divElement = child as ReactElement<{children?: ReactNode}>;
        
        return React.cloneElement(child as React.ReactElement<{ children?: ReactNode }>, {
          children: React.Children.map(divElement.props.children, (grandchild, grandchildIndex) => (
            <motion.div
              key={grandchildIndex}
              variants={childVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              custom={index + grandchildIndex}
              className="relative"
            >
              {grandchild}
            </motion.div>
          ))
        } as { children: ReactNode });
      }

      return (
        <motion.div
          key={index}
          variants={childVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          custom={index}
          className="relative w-full flex-1"
          style={{ minHeight: 'inherit' }}
        >
          {child}
        </motion.div>
      );
    });
  };

  return (
    <motion.div 
      ref={ref}
      className={`relative flex flex-col h-full ${className}`.trim()}
      style={{
        ...style,
        minHeight: 'inherit'
      }}
    >
      {renderChildren(children)}
    </motion.div>
  );
};

export default FramerReveal;